// Rehber (Directory) module — enum definitions, Turkish labels, colours and
// helpers shared by the directory pages. Enum keys mirror the CHECK constraints
// in directory-setup.sql; keep the two in sync.

export const DORMANT_DAYS = 60;

export const CONTACT_KINDS = {
  network: 'Network',
  supplier: 'Taşeron / Tedarikçi',
};

export const INTRODUCED_BY = {
  hamit_turkish_network: 'Hamit · Türk network',
  hamit_existing_customer_outreach: 'Hamit · müşteri outreach',
  cagri_direct: 'Cagri · doğrudan',
  customer_referral: 'Müşteri tavsiyesi',
  linkedin_inbound: 'LinkedIn',
  google_ads: 'Google Ads',
  architect_channel: 'Architect referral',
  other: 'Diğer',
};

// Short form used in list-card subtitles, e.g. "Hamit tanıttı · 14 May".
export const INTRODUCED_BY_SHORT = {
  hamit_turkish_network: 'Hamit tanıttı',
  hamit_existing_customer_outreach: 'Hamit tanıttı',
  cagri_direct: 'Cagri',
  customer_referral: 'Müşteri tavsiyesi',
  linkedin_inbound: 'LinkedIn',
  google_ads: 'Google Ads',
  architect_channel: 'Architect referral',
  other: 'Tanışma',
};

export const RELATIONSHIP_TYPES = {
  can_bring_work: 'Bize iş getirebilir',
  mutual_referral: 'Karşılıklı / referans kaynağı',
  relationship_value: 'İlişki değerli',
};

export const NETWORK_STATUSES = {
  new: 'Yeni tanıştık',
  following_up: 'Takipte',
  discussing_work: 'İş konuşuyoruz',
  work_won: 'İş aldık',
  closed: 'Kapandı',
};

export const SUPPLIER_STATUSES = {
  potential: 'Aday',
  used: 'Çalıştık',
  closed: 'Kapandı',
};

// Network statuses that count as "active" — only these can derive to dormant.
const ACTIVE_NETWORK_STATUSES = ['new', 'following_up', 'discussing_work'];

export function statusesForKind(kind) {
  return kind === 'supplier' ? SUPPLIER_STATUSES : NETWORK_STATUSES;
}

export function defaultStatusForKind(kind) {
  return kind === 'supplier' ? 'potential' : 'new';
}

export function initials(fullName) {
  if (!fullName) return '?';
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function daysSince(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

// Dormant is derived, never stored. Network contacts only: an active-status
// contact with no touch in DORMANT_DAYS days is "dormant".
export function isDormant(contact) {
  if (!contact || contact.contact_kind !== 'network') return false;
  if (!ACTIVE_NETWORK_STATUSES.includes(contact.status)) return false;
  const d = daysSince(contact.last_contact_date || contact.first_met_date);
  return d != null && d >= DORMANT_DAYS;
}

// Display label for a contact's current state (dormant overrides stored status).
export function statusLabel(contact) {
  if (isDormant(contact)) return 'Sessiz';
  const map = statusesForKind(contact.contact_kind);
  return map[contact.status] || contact.status || '—';
}

// Badge { color, bg } CSS-var pair for a contact's state.
export function statusStyle(contact) {
  if (isDormant(contact)) return { color: 'var(--text3)', bg: 'var(--bg3)' };
  switch (contact.status) {
    case 'discussing_work':
    case 'work_won':
    case 'used':
      return { color: 'var(--green)', bg: 'var(--green-dim)' };
    case 'following_up':
      return { color: 'var(--yellow)', bg: 'var(--yellow-dim)' };
    case 'new':
    case 'potential':
      return { color: 'var(--blue)', bg: 'var(--blue-dim)' };
    default: // closed
      return { color: 'var(--text3)', bg: 'var(--bg3)' };
  }
}

// Avatar background by relationship_type (network) or a neutral tone (supplier).
export function avatarStyle(contact) {
  if (!contact || contact.contact_kind === 'supplier') {
    return { color: 'var(--text2)', bg: 'var(--bg3)' };
  }
  switch (contact.relationship_type) {
    case 'can_bring_work':     return { color: 'var(--blue)', bg: 'var(--blue-dim)' };
    case 'mutual_referral':    return { color: 'var(--purple)', bg: 'var(--purple-dim)' };
    case 'relationship_value': return { color: 'var(--pink)', bg: 'var(--pink-dim)' };
    default:                   return { color: 'var(--text2)', bg: 'var(--bg3)' };
  }
}

export function fmtDate(isoDate, opts) {
  if (!isoDate) return '—';
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR', opts || { day: '2-digit', month: 'short', year: 'numeric' });
}

export function fmtDateShort(isoDate) {
  return fmtDate(isoDate, { day: '2-digit', month: 'short' });
}

export function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// Who Hamit/Cagri etc. — derived from the signed-in user, stored on notes.
export function userDisplayName(user) {
  return user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Bilinmiyor';
}
